import type { FilterInput } from '../constants';
import type { AnimalSearchFieldLookup } from './constants';
import { parseI6LookupString } from './parseI6LookupString';

export type ExpandedI6LookupFilters = {
  filters: FilterInput[];
  andGroups: FilterInput[][];
  orGroups: FilterInput[][];
};

/** Lookups that can expand to a real GraphQL predicate. */
export function lookupsWithI6Mapping(
  lookups: AnimalSearchFieldLookup[] | undefined | null,
): AnimalSearchFieldLookup[] {
  if (!lookups?.length) return [];
  return lookups.filter((l) => !!l.i6LookupString?.trim());
}

export class UnmappedI6LookupError extends Error {
  readonly markerField: string;
  readonly code: string;

  constructor(markerField: string, code: string) {
    super(
      `Selected filter "${code}" is not available in BreedLOGIC yet (missing i6 lookup mapping for ${markerField}).`,
    );
    this.name = 'UnmappedI6LookupError';
    this.markerField = markerField;
    this.code = code;
  }
}

/** True when expansion threw because a Select-if lookup has no i6 mapping. */
export function isUnmappedI6LookupError(
  error: unknown,
): error is UnmappedI6LookupError {
  return (
    error instanceof UnmappedI6LookupError ||
    (error instanceof Error && error.name === 'UnmappedI6LookupError')
  );
}

/**
 * Replace `__lookup__<field>` marker rows with filters parsed from the
 * selected lookup's `i6LookupString`. Compound lookups retain their own AND/OR
 * group so the surrounding search operator cannot change their meaning.
 *
 * ILRX-4434: selected markers without a mapping fail closed (throw) instead
 * of being silently dropped — that previously made Female + "Animal is a Sire"
 * behave as Sex=F only.
 */
export function expandI6LookupMarkerFilters(
  filters: FilterInput[],
  resolver: Map<string, AnimalSearchFieldLookup[]>,
  prefix: string,
): ExpandedI6LookupFilters {
  const expanded: FilterInput[] = [];
  const andGroups: FilterInput[][] = [];
  const orGroups: FilterInput[][] = [];

  for (const f of filters) {
    if (!f.field?.startsWith(prefix)) {
      expanded.push(f);
      continue;
    }
    if (!f.value) continue;

    const lookups = resolver.get(f.field);
    if (!lookups) {
      throw new UnmappedI6LookupError(f.field, f.value);
    }

    const choice = lookups.find((l) => l.code === f.value);
    if (!choice?.i6LookupString) {
      throw new UnmappedI6LookupError(f.field, f.value);
    }

    const parsed = parseI6LookupString(
      choice.i6LookupString,
      choice.i6LookupValueEncoding,
    );
    if (parsed.filters.length === 0) {
      throw new UnmappedI6LookupError(f.field, f.value);
    }

    if (parsed.logicalOperator === 'Or' && parsed.filters.length > 1) {
      orGroups.push(parsed.filters);
    } else if (parsed.filters.length > 1) {
      andGroups.push(parsed.filters);
    } else {
      expanded.push(...parsed.filters);
    }
  }

  return { filters: expanded, andGroups, orGroups };
}
